export const API_ENDPOINT = "https://nznclbxod6.execute-api.ap-southeast-2.amazonaws.com/Prod/";

const authHeaders = (token?: string): Record<string, string> =>
  token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};

export interface ScanHistoryItem {
  scanNumber: string;
  timestamp: string;
  riskScore: string;
  riskLevel: string;
  userId?: string;
  authType?: string;
}

export interface AnalysisResult {
  melanoma_risk_score: number;
  risk_level: string;
  history?: ScanHistoryItem[];
}

export const fetchHistory = async (token?: string): Promise<ScanHistoryItem[]> => {
  try {
    const response = await fetch(`${API_ENDPOINT}history`, {
      headers: {
        ...authHeaders(token),
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch history: ${response.status}`);
    }
    const data = await response.json();
    return data.history || [];
  } catch (err) {
    console.error("Error fetching history:", err);
    return [];
  }
};

export const analyzeImage = async (file: File, token?: string): Promise<AnalysisResult> => {
  // Always use image/jpeg for consistency with presigned URL signing
  const contentType = "image/jpeg";

  // 1. Get Presigned URL
  console.log("[Step 1] Fetching presigned URL from:", `${API_ENDPOINT}presigned-url`);
  let presignedResponse;
  try {
    presignedResponse = await fetch(`${API_ENDPOINT}presigned-url?contentType=${encodeURIComponent(contentType)}`);
  } catch (err) {
    console.error("[Step 1] Network error fetching presigned URL:", err);
    throw new Error(`Step 1 Failed: Network error - ${err}`);
  }
  
  if (!presignedResponse.ok) {
      const text = await presignedResponse.text();
      console.error("[Step 1] API error:", presignedResponse.status, text);
      throw new Error(`Failed to get upload URL: ${presignedResponse.status} ${text}`);
  }
  const { uploadUrl, key } = await presignedResponse.json();
  console.log("[Step 1] Success! Got key:", key);
  console.log("[Step 2] Upload URL:", uploadUrl);

  // 2. Upload Image to S3
  // Using XMLHttpRequest as a fallback since fetch() can be blocked by browser shields
  console.log("[Step 2] Uploading image to S3...");
  
  try {
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl, true);
      xhr.setRequestHeader("Content-Type", contentType);
      
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          console.log("[Step 2] Success! Image uploaded via XHR.");
          resolve();
        } else {
          console.error("[Step 2] S3 error:", xhr.status, xhr.statusText);
          reject(new Error(`Failed to upload image: ${xhr.status}`));
        }
      };
      
      xhr.onerror = () => {
        console.error("[Step 2] XHR network error");
        reject(new Error("Step 2 Failed: S3 upload network error (XHR)"));
      };
      
      xhr.send(file);
    });
  } catch (err) {
    console.error("[Step 2] Upload failed:", err);
    throw err;
  }

  // 3. Analyze
  console.log("[Step 3] Calling analyze endpoint...");
  let analyzeResponse;
  try {
    analyzeResponse = await fetch(`${API_ENDPOINT}analyze`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(token),
      },
      body: JSON.stringify({ key }),
    });
  } catch (err) {
    console.error("[Step 3] Network error calling analyze:", err);
    throw new Error(`Step 3 Failed: Analyze network error - ${err}`);
  }
  
  if (!analyzeResponse.ok) {
      const text = await analyzeResponse.text();
      console.error("[Step 3] API error:", analyzeResponse.status, text);
      throw new Error(`Failed to analyze image: ${analyzeResponse.status} ${text}`);
  }
  console.log("[Step 3] Success! Got analysis result.");

  const result = await analyzeResponse.json();
  
  // Parse the score to a number if it's a string
  if (typeof result.melanoma_risk_score === 'string') {
      result.melanoma_risk_score = parseFloat(result.melanoma_risk_score);
  }
  
  return result;
};
